import { useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import Reveal from '../components/Reveal';
import HeartDivider from '../components/HeartDivider';
import StatCounter from '../components/StatCounter';
import HighlightsSection from '../components/HighlightsSection';
import SoundToggleVideo from '../components/SoundToggleVideo';
import { useAsync } from '../lib/useAsync';
import { storiesApi, eventsApi, activitiesApi, newsApi, clubsApi, membersApi } from '../api/resources';
import { assetUrl } from '../api/client';
import { formatDate, truncate, cx } from '../lib/format';
import { STATIC_GALLERY, fallbackImage } from '../lib/fallbackImages';

// Zonta International Core Values (Area 1 deck, slide 24).
const CORE_VALUES = [
  { icon: 'globe', title: 'Global Community' },
  { icon: 'handshake', title: 'Friendship' },
  { icon: 'users', title: 'Equality & Inclusion' },
  { icon: 'handHeart', title: 'Respect' },
  { icon: 'ribbon', title: 'Integrity' },
];

// ZI Strategic Plan, Biennium 2026-2028 — Four Strategic Goals (deck slide 25).
const STRATEGIC_GOALS = [
  { num: '01', title: 'Credible & Visible Voice', text: 'Strong advocacy for women and girls.' },
  { num: '02', title: 'Club Success', text: 'Strong, vibrant and sustainable clubs.' },
  { num: '03', title: 'Leadership & Sustainability', text: 'Develop leaders and ensure organizational sustainability.' },
  { num: '04', title: 'Engagement of Audiences & Allies', text: 'Build partnerships and expand our impact.' },
];

// The values that guide us — S·H·A·R·E (deck slide 27). Shown below the hero.
const SHARE = [
  { letter: 'S', label: 'Sustainability' },
  { letter: 'H', label: 'Human Rights' },
  { letter: 'A', label: 'Accountability' },
  { letter: 'R', label: 'Respect' },
  { letter: 'E', label: 'Empowerment' },
];

// From Your Area Director — Our Commitment (deck slide 9).
const COMMITMENTS = [
  'Listen and act with an open heart.',
  'Lead with integrity and compassion.',
  'Empower and support our clubs and their leaders.',
  'Foster collaboration and friendship among clubs.',
  'Celebrate our successes — and learn from our challenges, together.',
];

/** First usable image URL for a content record (featured image, else null). */
function imgOf(x) {
  if (!x) return null;
  return assetUrl(x.featured_image_path) || x.featured_image_url || null;
}

/**
 * Image for a highlight card. `assetUrl` passes absolute URLs (like the API's
 * `first_image_url`) straight through and resolves storage-relative paths, so
 * the caller just hands over its best candidate. Records with no image at all
 * rotate through the shipped site photos (`lib/fallbackImages`) rather than
 * showing an empty box.
 *
 * This only covers a *missing* reference. A reference that exists but 404s is
 * `HighlightsSection`'s job — it swaps to `fallbackImage(i)` on the image's
 * error event, i.e. to exactly the photo this function would have picked.
 */
function cardImage(url, i) {
  return assetUrl(url) || fallbackImage(i);
}

/* Small heart glyph for commitment bullets (decorative). */
function HeartBullet() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="mt-0.5 h-4 w-4 shrink-0 text-gold-500" fill="currentColor">
      <path d="M12 21C7 16.6 3 13.3 3 9.3 3 6.4 5.2 4.5 7.7 4.5c1.7 0 3.2.9 4.3 2.4 1.1-1.5 2.6-2.4 4.3-2.4 2.5 0 4.7 1.9 4.7 4.8 0 4-4 7.3-9 11.7Z" />
    </svg>
  );
}

/* Simple stroke icon set (decorative — always rendered aria-hidden). */
const ICON_PATHS = {
  users: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  handHeart: (
    <>
      <path d="M19.5 9.5c.7-.7 1.5-1.6 1.5-2.7A2.73 2.73 0 0 0 16 5a2.78 2.78 0 0 0-5 1.8c0 1.2.8 2 1.5 2.8L16 13Z" />
      <path d="M2 15l6 6" />
      <path d="m7 20 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.2-4a2 2 0 0 0-2.75-2.9l-2.6 2.4" />
      <path d="M11 16h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 18" />
    </>
  ),
  handshake: (
    <>
      <path d="m11 17 2 2a1 1 0 1 0 3-3" />
      <path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4" />
      <path d="M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3" />
      <path d="M3 4h8" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
      <path d="M2 12h20" />
    </>
  ),
  ribbon: (
    <>
      <path d="M12 3c2.5 0 4 1.7 4 3.8 0 3.2-2.6 5.4-4 6.9-1.4-1.5-4-3.7-4-6.9C8 4.7 9.5 3 12 3Z" />
      <path d="m10.4 12.6-4.2 8.4 3.6-1 2.2-3.2" />
      <path d="m13.6 12.6 4.2 8.4-3.6-1-2.2-3.2" />
    </>
  ),
};

function Icon({ name, className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      {ICON_PATHS[name]}
    </svg>
  );
}

/* Time shown only when the event has a real time-of-day (midnight = date-only). */
function eventTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime()) || (d.getHours() === 0 && d.getMinutes() === 0)) return '';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/**
 * One row of the Upcoming Events aside.
 *
 * `title` and `location` are both `max:255` free-text columns, so both get the
 * same two-part treatment every highlight band already has:
 *
 *  - `truncate(…, 90)` bounds how much is rendered — the same 90-char budget the
 *    bands use, so the two views of the same event agree on length;
 *  - `break-words` lets a long *unbroken* token wrap. This is the half that
 *    actually stops horizontal page overflow: capping a 200-char run of "Z" at
 *    90 still leaves a 90-char word that `overflow-wrap: normal` refuses to
 *    break, and at 390px that single word stretched the whole document to
 *    ~1768px wide. The enclosing `min-w-0` is not enough on its own — it clears
 *    the flex item's automatic minimum size, but unbreakable text still paints
 *    past the box and lands in the document's scrollable overflow.
 */
function EventRow({ event }) {
  const day = new Date(event.starts_at).getDate();
  const time = eventTime(event.starts_at);
  return (
    <div className="flex gap-4">
      <div className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-lg bg-maroon-700 text-white">
        <span className="text-[0.6rem] font-bold uppercase tracking-widest text-gold-300">
          {formatDate(event.starts_at, { month: 'short', day: undefined, year: undefined })}
        </span>
        <span className="text-xl font-extrabold leading-none">{Number.isNaN(day) ? '—' : day}</span>
      </div>
      <div className="min-w-0">
        <p className="break-words font-semibold leading-snug text-ink">{truncate(event.title, 90)}</p>
        {/* `|| fallback` sits OUTSIDE truncate: truncate('') is '', so an event
            with no location still reads "Details to be announced". */}
        <p className="mt-0.5 break-words text-sm text-ink-muted">
          {truncate(event.location, 90) || 'Details to be announced'}
        </p>
        {time && <p className="text-sm text-ink-muted">{time}</p>}
      </div>
    </div>
  );
}

export default function Landing() {
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const newsPosts = useAsync(() => newsApi.list({ per_page: 4 }), []);
  const featured = useAsync(() => storiesApi.list({ featured: 1, per_page: 5 }), []);
  const allStories = useAsync(() => storiesApi.list({ per_page: 5 }), []);
  const events = useAsync(() => eventsApi.list({ upcoming: 1, per_page: 4 }), []);
  const pastEvents = useAsync(() => eventsApi.list({ past: 1, per_page: 4 }), []);
  const activities = useAsync(() => activitiesApi.list({ per_page: 4 }), []);
  const clubs = useAsync(() => clubsApi.list(), []);
  const leaders = useAsync(() => membersApi.list({ leadership: 1, per_page: 60 }), []);

  // --- Impact stats (derived from real data; falls back to honest labels) ---
  const clubList = clubs.data || [];
  const clubCount = clubList.length;
  const memberTotal = clubList.reduce((n, c) => n + (Number(c.members_count) || 0), 0);
  const leaderCount = leaders.data?.meta?.total ?? leaders.data?.items?.length ?? 0;
  const statsLoading = clubs.loading || leaders.loading;
  const stats = [
    clubCount > 0 && { label: 'Member Clubs', value: clubCount },
    memberTotal > 0 && { label: 'Zonta Members', value: memberTotal, suffix: '+' },
    leaderCount > 0 && { label: 'Board Leaders', value: leaderCount },
    { label: 'Biennium', text: '2026–2028' },
    { label: 'Strategic Goals', value: 4 },
  ]
    .filter(Boolean)
    .slice(0, 4);
  const statsCols =
    stats.length >= 4 ? 'grid-cols-2 sm:grid-cols-4' : stats.length === 3 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-2';

  // --- Published-only guard --------------------------------------------------
  // DO NOT REMOVE `isPublished` as "redundant with the API". It is not.
  // `api/client.js` sends `withCredentials: true`, so a signed-in viewer's
  // session cookie rides along with these public GETs — and Story/News/Activity/
  // Event `index` only apply `scopePublic()` for guests: a member additionally
  // sees their own records in any status, and an administrator gets NO status
  // scope at all. Without this filter, an administrator loading the public home
  // page sees drafts, pending and even rejected records rendered as ordinary
  // marketing cards. A request parameter cannot do the job either —
  // `EventController@index` has no `?status=` branch — so filtering here is the
  // only rule that holds uniformly across all four endpoints and every role.
  //
  // Accepted trade-off: when unapproved records occupy the API page, a signed-in
  // administrator may see fewer cards than the slot allows. Correctness beats
  // card count, and it affects administrators only — guests are unaffected.
  //
  // EVERY list on this public page runs through it: the featured-stories
  // editorial block, the Upcoming Events aside, and all three highlight bands.
  //
  // Declared ABOVE the first list that uses it. It used to sit below the
  // featured-stories block, and that block was the one list on the page that
  // never called it — an administrator's own drafts and any pending story led
  // the home page as the hero story. Measured with a mixed status set: the
  // draft and the pending story both rendered, in an admin session and in a
  // guest session. Keep this declaration first so the next list added here
  // cannot repeat that by being written above the guard.
  const isPublished = (x) => x?.status === 'published';

  // --- Featured stories (editorial: one lead + a short list) ---
  // Filtered before the `featured`/`allStories` fallback is chosen and before
  // any `slice`, so an unapproved story can neither lead the page nor occupy a
  // slot that then vanishes.
  const featuredStories = (featured.data?.items || []).filter(isPublished);
  const anyStories = (allStories.data?.items || []).filter(isPublished);
  const storyList = featuredStories.length ? featuredStories : anyStories;
  const heroStory = storyList[0];
  const restStories = storyList.slice(1, 4);
  const storiesLoading = featured.loading && allStories.loading;

  // Shared by the Upcoming Events aside and the Events & Highlights band, so the
  // two can never disagree about what the public is allowed to see. Filtered
  // here, before any `slice`, so a draft cannot occupy a slot and then vanish.
  const upcomingEvents = (events.data?.items || []).filter(isPublished);

  // --- Highlight bands -------------------------------------------------------
  // There is deliberately no cross-type fallback: a band with nothing to show
  // says so rather than borrowing another type's content.

  const newsCards = (newsPosts.data?.items || []).filter(isPublished).map((n, i) => ({
    id: n.id,
    to: `/news/${n.slug || n.id}`,
    image: cardImage(n.featured_image_path || n.featured_image_url, i),
    // Month + year: for a news post the exact publication day is not the point.
    badge: n.published_at ? formatDate(n.published_at, { day: undefined }) : null,
    title: n.title,
    text: truncate(n.excerpt || n.body, 90),
    pill: null,
  }));

  const activityCards = (activities.data?.items || []).filter(isPublished).map((a, i) => ({
    id: a.id,
    to: `/activities/${a.slug || a.id}`,
    image: cardImage(a.first_image_url || a.image_path, i),
    // Full date: for a service project the day it happened IS the point.
    badge: a.occurred_on ? formatDate(a.occurred_on) : null,
    title: a.title,
    text: truncate(a.summary || a.description, 90),
    pill: null,
  }));

  // Up to two of the soonest upcoming events lead the band; the rest is
  // backfilled with the most recently concluded ones, then with any remaining
  // upcoming ones. That keeps the band from duplicating the Upcoming Events
  // aside above, and guarantees past events — the ones that actually carry
  // galleries and recaps — always have somewhere to appear. The two sources are
  // disjoint by definition (starts_at >= now vs < now), so no dedupe is needed.
  // Both sources are filtered BEFORE the composition so the mix rule operates on
  // publishable records only — otherwise an administrator's two draft events
  // would take the upcoming slots and then be dropped, leaving a short band.
  const pastEventItems = (pastEvents.data?.items || []).filter(isPublished);
  const eventCards = [...upcomingEvents.slice(0, 2), ...pastEventItems, ...upcomingEvents.slice(2)]
    .slice(0, 4)
    .map((e, i) => ({
      id: e.id,
      to: `/events/${e.slug || e.id}`,
      image: cardImage(e.first_image_url || e.banner_image_path, i),
      badge: e.starts_at ? formatDate(e.starts_at) : null,
      title: e.title,
      // `location` is capped on the same 90-char budget as every other band:
      // the column allows 255 chars, and an untruncated one overflowed the
      // whole page sideways on mobile.
      text: truncate(e.location || e.description, 90),
      pill: e.recaps_count > 0 ? 'Recap' : null,
    }));

  return (
    <>
      <Seo
        title="Leading with Heart. Serving with Purpose."
        description="Zonta District 17 Area 1 — one Area, one purpose, greater impact. Building strong clubs and empowering women and girls."
        type="website"
      />

      {/* HERO — cream field, right-bleed photo, maroon wave flowing into SHARE */}
      <section className="relative overflow-hidden bg-cream-100">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 text-maroon-700/[0.04] pattern-grid" />

        <div
          aria-hidden="true"
          className="absolute inset-y-0 right-0 hidden w-[55%] lg:block"
          style={{
            maskImage: 'linear-gradient(to right, transparent 0%, black 32%)',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 32%)',
          }}
        >
          <img src="/img/community.jpg" alt="" className="h-full w-full object-cover" />
        </div>

        <div className="container-page relative z-10">
          <div className="flex flex-col justify-center py-14 sm:py-16 lg:min-h-[32rem] lg:max-w-[48%] lg:py-24">
            <Reveal as="p" className="text-xs font-semibold uppercase tracking-[0.3em] text-maroon-700 sm:text-sm">
              Welcome to
            </Reveal>
            <Reveal
              as="h1"
              delay={80}
              className="mt-3 font-serif text-4xl font-bold leading-[1.05] text-maroon-800 sm:text-5xl xl:text-6xl"
            >
              District 17 &ndash; Area&nbsp;1
            </Reveal>
            <Reveal
              as="p"
              delay={160}
              className="mt-4 font-serif text-2xl italic leading-snug text-gold-600 sm:text-3xl xl:text-4xl"
            >
              Leading with Heart.
              <br />
              Serving with Purpose.
            </Reveal>
            <Reveal delay={220}>
              <HeartDivider className="mt-6" />
            </Reveal>
            <Reveal
              as="p"
              delay={280}
              className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-1.5 font-bold text-maroon-800"
            >
              One Area
              <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-gold-500" />
              One Purpose
              <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-gold-500" />
              Greater Impact
            </Reveal>
            <Reveal as="p" delay={340} className="mt-2 text-ink-soft">
              Building Strong Clubs. Empowering Women and Girls.
            </Reveal>
            <Reveal delay={420} className="mt-8 flex flex-wrap gap-3">
              <Link to="/leadership" className="btn-primary">
                Meet Area 1 Officers &amp; Presidents
                <span aria-hidden="true">&rsaquo;</span>
              </Link>
              <Link to="/about" className="btn-outline">
                Our Mission
              </Link>
            </Reveal>
          </div>
        </div>

        <div className="relative lg:hidden">
          <img
            src="/img/community.jpg"
            alt="Zonta members collaborating on a service project."
            className="h-64 w-full object-cover sm:h-80"
          />
        </div>

        <div aria-hidden="true" className="relative z-10 -mt-12 sm:-mt-16 lg:mt-0">
          <svg viewBox="0 0 1440 120" preserveAspectRatio="none" className="block h-16 w-full sm:h-20 lg:h-28">
            <defs>
              <linearGradient id="heroWaveGrad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#800000" />
                <stop offset="100%" stopColor="#440000" />
              </linearGradient>
            </defs>
            <path
              d="M0,64 C260,110 520,108 760,78 C1000,48 1240,10 1440,36"
              fill="none"
              stroke="#f0b429"
              strokeWidth="7"
            />
            <path
              d="M0,72 C260,118 520,116 760,86 C1000,56 1240,18 1440,44 L1440,120 L0,120 Z"
              fill="url(#heroWaveGrad)"
            />
          </svg>
        </div>
      </section>

      {/* THE VALUES THAT GUIDE US — S·H·A·R·E (deck slide 27), directly below hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-maroon-800 via-maroon-900 to-maroon-950 text-cream-100">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 text-gold-300/10 pattern-dots" />
        <div className="container-page relative py-14 sm:py-16">
          <Reveal className="text-center">
            <p className="eyebrow text-gold-400">The Values That Guide Us</p>
            <h2 className="mt-2 font-serif text-3xl font-bold text-cream-50 sm:text-4xl">
              S<span className="text-gold-400">&middot;</span>H<span className="text-gold-400">&middot;</span>A
              <span className="text-gold-400">&middot;</span>R<span className="text-gold-400">&middot;</span>E
            </h2>
          </Reveal>

          <div className="mx-auto mt-11 grid max-w-5xl grid-cols-2 gap-x-6 gap-y-9 sm:grid-cols-5 sm:gap-x-4">
            {SHARE.map((s, i) => (
              <Reveal key={s.letter} delay={i * 80} className="group flex flex-col items-center text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/5 font-serif text-4xl font-bold text-gold-400 ring-1 ring-gold-300/30 transition duration-300 group-hover:-translate-y-1 group-hover:bg-gold-400/10 sm:h-16 sm:w-16 sm:text-5xl">
                  {s.letter}
                </span>
                <span className="mt-3 text-xs font-bold uppercase tracking-[0.14em] text-cream-100 sm:text-sm">
                  {s.label}
                </span>
              </Reveal>
            ))}
          </div>

          <Reveal className="mx-auto mt-10 max-w-2xl text-center text-sm text-cream-200/90">
            A reminder of the values that strengthen our clubs and our leadership.
          </Reveal>
        </div>
      </section>

      {/* WELCOME — director card | message + Area Director's commitment | events */}
      <section className="section">
        <div className="container-page grid gap-10 md:grid-cols-[280px_minmax(0,1fr)] lg:grid-cols-[300px_minmax(0,1.55fr)_minmax(0,1fr)]">
          {/* Area Director card with offset gold frame */}
          <Reveal className="mx-auto w-full max-w-xs md:mx-0 md:max-w-none">
            <div className="relative">
              <div aria-hidden="true" className="absolute -inset-2.5 rounded-[1.4rem] border-2 border-gold-400/70" />
              <div className="relative overflow-hidden rounded-2xl shadow-lift">
                <img
                  src="/img/director.jpg"
                  alt="Azucena 'Zeni' Yao, Area 1 Director of District 17."
                  className="aspect-[3/4] w-full object-cover"
                />
                <div className="bg-maroon-800 p-6 text-cream-100">
                  <p className="font-serif text-3xl italic text-gold-300">Azucena Yao</p>
                  <p className="mt-2.5 text-[0.7rem] font-semibold uppercase leading-relaxed tracking-[0.18em] text-cream-200">
                    Area 1 Director
                    <br />
                    District 17 &ndash; Area 1
                    <br />
                    Biennium 2026&ndash;2028
                  </p>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Welcome message + commitment */}
          <Reveal delay={100}>
            <p className="eyebrow text-gold-600">A Message of Welcome</p>
            <h2 className="mt-2 font-serif text-3xl font-bold text-maroon-800 sm:text-4xl">
              Welcome to District 17 &ndash; Area 1
            </h2>
            <HeartDivider className="mt-4" />
            <div id="welcome-message" className="mt-5 max-w-prose space-y-4 leading-relaxed text-ink-soft">
              <p>
                It is both an honor and a privilege to serve alongside our dedicated Club Presidents and members as we begin a new biennium of leadership, service, advocacy, and fellowship.
              </p>
              <p>
                We live in a time of unprecedented challenges and extraordinary opportunities. More than ever, women and girls need strong voices, compassionate leaders, and communities working together to create meaningful and lasting change.
              </p>
              {welcomeOpen && (
                <>
                  <p>
                    As members of Zonta, we are united by a common purpose, to build a better world for women and girls. While each of our clubs has its own unique strengths and traditions, we are strongest when we work together, learn from one another, and support one another in advancing our shared mission.
                  </p>
                  <p>
                    This website was created to be more than a source of information. It is our platform for communication, collaboration, and celebration, a place where we share ideas, recognize achievements, and strengthen the bonds that unite our Area.
                  </p>
                  <p>
                    For this biennium, I invite all of us to embrace collective leadership by building strong clubs, nurturing future leaders, empowering our members, and expanding our impact in the communities we serve.
                  </p>
                  <p>
                    Let us move forward with one heart and one purpose, leading with integrity, serving with compassion, and creating lasting impact through service and advocacy.
                  </p>
                  <p>
                    Together, let us inspire, empower, and make a meaningful difference in the lives of women and girls.
                  </p>
                </>
              )}
            </div>
            <button
              type="button"
              onClick={() => setWelcomeOpen((v) => !v)}
              aria-expanded={welcomeOpen}
              aria-controls="welcome-message"
              className="link-caps mt-4"
            >
              {welcomeOpen ? 'Show less' : 'Show more'}
              <span aria-hidden="true">{welcomeOpen ? '▴' : '▾'}</span>
            </button>

            {/* Our Commitment — deck slide 9 */}
            <div className="mt-8 rounded-2xl border border-cream-300 bg-surface-alt p-6">
              <p className="eyebrow text-gold-600">From Your Area Director</p>
              <h3 className="mt-1 font-serif text-2xl font-bold text-maroon-800">Our Commitment</h3>
              <ul className="mt-4 space-y-2.5">
                {COMMITMENTS.map((c) => (
                  <li key={c} className="flex gap-2.5 text-sm leading-relaxed text-ink-soft">
                    <HeartBullet />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-5 font-serif text-lg italic leading-snug text-maroon-700">
                Together, let us build strong clubs, develop strong leaders, and create greater impact.
              </p>
              <p className="mt-1.5 text-sm font-semibold text-ink-soft">You are not walking this journey alone.</p>
            </div>

            <div>
              <Link to="/leadership" className="btn-primary mt-8">
                Message from the Area Director
                <span aria-hidden="true">&rsaquo;</span>
              </Link>
            </div>
          </Reveal>

          {/* Upcoming events */}
          {/* `min-w-0` is the structural half of the overflow fix, and it belongs
              HERE, on the grid item — not on the inner div, which already had it.
              At >=md this grid has explicit `280px` / `minmax(0,1fr)` tracks whose
              min sizing functions are not `auto`, so an item's automatic minimum
              size is already 0. At mobile the grid falls back to ONE implicit
              `auto` track, and there the item's automatic minimum size becomes its
              content-based minimum — i.e. the widest unbreakable word in the
              aside. `break-words` does not help with that: `overflow-wrap:
              break-word` breaks a word to fit a line, but is ignored when the
              browser computes min-content. So a 90-char event title still sized
              the column to ~865px and dragged the whole document with it.
              `min-w-0` overrides the auto minimum; `break-words` inside then wraps
              the word into the correctly-sized box. Both are required.
              Same pairing as `HighlightsSection`'s grid items. */}
          <Reveal delay={180} className="min-w-0 md:col-span-2 lg:col-span-1">
            <aside className="card relative overflow-hidden p-6">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute right-0 top-0 h-1 w-full bg-gradient-to-r from-maroon-700 to-gold-400"
              />
              <h2 className="font-serif text-2xl font-bold text-maroon-800">Upcoming Events</h2>
              <div className="mt-5 space-y-5">
                {events.loading ? (
                  Array.from({ length: 3 }).map((_, i) => <div key={i} className="skeleton h-14 w-full rounded-lg" />)
                ) : upcomingEvents.length ? (
                  upcomingEvents.slice(0, 3).map((e) => <EventRow key={e.id} event={e} />)
                ) : (
                  <p className="text-sm text-ink-muted">No upcoming events scheduled.</p>
                )}
              </div>
              <Link
                to="/events"
                className="mt-6 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.16em] text-maroon-700 transition hover:text-gold-600"
              >
                View all events
                <span aria-hidden="true">&rsaquo;</span>
              </Link>
            </aside>
          </Reveal>
        </div>
      </section>

      {/* ZONTA PRAYER AND HYMN — a quiet moment after the welcome. Muted until asked. */}
      <section className="section relative overflow-hidden bg-cream-100">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-gold-100/50 blur-2xl"
        />
        <div className="container-page relative">
          <Reveal className="mx-auto max-w-2xl text-center">
            <p className="eyebrow text-gold-600">A Moment Together</p>
            <h2 className="mt-2 font-serif text-3xl font-bold text-maroon-800 sm:text-4xl">
              Zonta Prayer and Hymn
            </h2>
            <HeartDivider className="mt-5 justify-center" />
            <p className="mt-4 text-ink-soft">
              The video plays quietly on its own. Turn on the sound to listen.
            </p>
          </Reveal>
          <Reveal delay={150} className="mx-auto mt-10 max-w-4xl">
            <SoundToggleVideo src="/video/zonta-prayer-and-hymn.mp4" label="Zonta Prayer and Hymn" />
          </Reveal>
        </div>
      </section>

      {/* STRATEGIC PLAN — Four Strategic Goals (deck slide 25) */}
      <section className="section relative overflow-hidden bg-surface-alt">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 text-gold-500/[0.05] pattern-dots" />
        <div className="container-page relative">
          <Reveal className="flex flex-col items-center text-center">
            <p className="eyebrow text-gold-600">Zonta International Strategic Plan</p>
            <h2 className="mt-2 font-serif text-3xl font-bold text-maroon-800 sm:text-4xl">Four Strategic Goals</h2>
            <p className="mt-2 text-xs font-bold uppercase tracking-[0.18em] text-ink-muted">Biennium 2026&ndash;2028</p>
            <HeartDivider className="mt-5" />
          </Reveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {STRATEGIC_GOALS.map((g, i) => (
              <Reveal
                key={g.num}
                delay={i * 90}
                className="group relative overflow-hidden rounded-2xl border border-cream-300 bg-surface-card p-6 shadow-soft transition duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <span aria-hidden="true" className="font-display text-5xl font-extrabold text-maroon-700/15">
                  {g.num}
                </span>
                <h3 className="mt-2 font-display text-lg font-bold text-maroon-800">{g.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-soft">{g.text}</p>
                <span
                  aria-hidden="true"
                  className="absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 bg-gradient-to-r from-maroon-700 to-gold-400 transition-transform duration-300 group-hover:scale-x-100"
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CORE VALUES — Zonta International Core Values (deck slide 24) */}
      <section className="relative border-y border-cream-300 bg-surface-card">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 text-maroon-700/[0.03] pattern-grid" />
        <div className="container-page relative py-16">
          <Reveal className="mb-11 text-center">
            <p className="eyebrow text-gold-600">What We Stand For</p>
            <h2 className="mt-2 font-serif text-3xl font-bold text-maroon-800 sm:text-4xl">
              Zonta International Core Values
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-3 lg:grid-cols-5">
            {CORE_VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 70} className="group flex flex-col items-center text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-maroon-50 text-maroon-700 ring-1 ring-gold-300/60 transition duration-300 group-hover:-translate-y-1 group-hover:bg-maroon-700 group-hover:text-gold-300 group-hover:ring-gold-400">
                  <Icon name={v.icon} className="h-8 w-8" />
                </span>
                <h3 className="mt-4 text-sm font-extrabold uppercase tracking-wide text-ink">{v.title}</h3>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* IMPACT STATS — bold maroon band with real Area data */}
      <section className="relative overflow-hidden bg-gradient-to-br from-maroon-800 via-maroon-900 to-maroon-950 text-cream-100">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 text-gold-300/10 pattern-dots" />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-24 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full bg-gold-400/10 blur-3xl"
        />
        <div className="container-page relative py-14 sm:py-16">
          <Reveal className="text-center">
            <p className="eyebrow text-gold-400">Our Area at a Glance</p>
            <h2 className="mt-2 font-serif text-3xl font-bold text-cream-50 sm:text-4xl">One Area, Greater Impact</h2>
          </Reveal>

          <div className={cx('mx-auto mt-11 grid max-w-4xl gap-x-6 gap-y-10', statsCols)}>
            {statsLoading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="flex flex-col items-center gap-3">
                    <div className="skeleton h-12 w-24 rounded-lg bg-white/10" />
                    <div className="skeleton h-3 w-20 rounded bg-white/10" />
                  </div>
                ))
              : stats.map((s, i) => (
                  <Reveal key={s.label} delay={i * 80} className="text-center">
                    {s.text ? (
                      <span className="font-display text-3xl font-extrabold text-gold-400 sm:text-4xl">{s.text}</span>
                    ) : (
                      <StatCounter
                        value={s.value}
                        suffix={s.suffix || ''}
                        className="font-display text-5xl font-extrabold text-gold-400 sm:text-6xl"
                      />
                    )}
                    <p className="mt-2 text-[0.7rem] font-bold uppercase tracking-[0.18em] text-cream-200 sm:text-xs">
                      {s.label}
                    </p>
                  </Reveal>
                ))}
          </div>
        </div>
      </section>

      {/* IMPACT STORIES — editorial lead + short list */}
      {(storiesLoading || storyList.length > 0) && (
        <section className="section bg-surface-card">
          <div className="container-page">
            <Reveal className="mb-9 flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="eyebrow text-gold-600">From Our Clubs</p>
                <h2 className="mt-2 font-serif text-3xl font-bold text-maroon-800 sm:text-4xl">Impact Stories</h2>
              </div>
              <Link to="/stories" className="link-caps">
                All stories <span aria-hidden="true">&rsaquo;</span>
              </Link>
            </Reveal>

            {storiesLoading ? (
              <div className="grid gap-8 lg:grid-cols-2">
                <div className="skeleton h-80 rounded-2xl" />
                <div className="space-y-4">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="skeleton h-24 rounded-xl" />
                  ))}
                </div>
              </div>
            ) : (
              <div className="grid gap-8 lg:grid-cols-2">
                {/* `min-w-0` for the same reason as the events aside: Tailwind's
                    `lg:grid-cols-2` expands to `minmax(0,1fr)` tracks and is safe,
                    but the mobile fallback is a single implicit `auto` track where
                    the item's content-based minimum applies. A story title is
                    `max:255` free text. */}
                <Reveal className="min-w-0">
                  <Link
                    to={`/stories/${heroStory.slug || heroStory.id}`}
                    className="card card-hover group block overflow-hidden"
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-maroon-50">
                      <img
                        src={imgOf(heroStory) || STATIC_GALLERY[0]}
                        alt=""
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    {/* Same rule as the aside and the bands: bound the length,
                        and allow an unbroken token to wrap. `Story.title` is
                        `max:255` and `excerpt` `max:500`, both free text. */}
                    <div className="min-w-0 p-6">
                      <h3 className="break-words font-serif text-2xl font-bold text-maroon-800 transition-colors group-hover:text-maroon-700">
                        {truncate(heroStory.title, 120)}
                      </h3>
                      <p className="mt-2 break-words text-ink-soft">{truncate(heroStory.excerpt || heroStory.body, 160)}</p>
                      <span className="link-caps mt-4">
                        Read the story <span aria-hidden="true">&rsaquo;</span>
                      </span>
                    </div>
                  </Link>
                </Reveal>

                <div className="flex min-w-0 flex-col justify-center gap-3">
                  {restStories.map((s, i) => (
                    <Reveal key={s.id} delay={i * 80} className="min-w-0">
                      <Link
                        to={`/stories/${s.slug || s.id}`}
                        className="group flex gap-4 rounded-xl p-2 transition hover:bg-maroon-50/70"
                      >
                        <div className="h-24 w-28 shrink-0 overflow-hidden rounded-lg bg-maroon-50">
                          <img
                            src={imgOf(s) || STATIC_GALLERY[(i + 1) % STATIC_GALLERY.length]}
                            alt=""
                            loading="lazy"
                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                        <div className="min-w-0 self-center">
                          <h4 className="break-words font-display font-bold leading-snug text-ink transition-colors group-hover:text-maroon-700">
                            {truncate(s.title, 90)}
                          </h4>
                          <p className="mt-1 break-words text-sm text-ink-muted">{truncate(s.excerpt || s.body, 80)}</p>
                        </div>
                      </Link>
                    </Reveal>
                  ))}
                  {restStories.length === 0 && (
                    <p className="text-sm text-ink-muted">More stories from our clubs are coming soon.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* NEWS & HIGHLIGHTS */}
      <HighlightsSection
        className="bg-surface-alt"
        eyebrow="Latest Updates"
        title="News & Highlights"
        viewAllTo="/news"
        viewAllLabel="View all news"
        loading={newsPosts.loading}
        items={newsCards}
        emptyTitle="No news published yet"
        emptyMessage="Check back soon for updates and highlights from our clubs."
      />

      {/* ACTIVITIES & HIGHLIGHTS */}
      <HighlightsSection
        className="bg-surface-card"
        eyebrow="In Our Community"
        title="Activities & Highlights"
        viewAllTo="/activities"
        viewAllLabel="View all activities"
        loading={activities.loading}
        items={activityCards}
        emptyTitle="No activities published yet"
        emptyMessage="Service projects from our clubs appear here once they are approved and published."
      />

      {/* EVENTS & HIGHLIGHTS */}
      <HighlightsSection
        className="bg-surface-alt"
        eyebrow="Mark Your Calendar"
        title="Events & Highlights"
        viewAllTo="/events"
        viewAllLabel="View all events"
        loading={events.loading || pastEvents.loading}
        items={eventCards}
        cta="See details"
        emptyTitle="No events published yet"
        emptyMessage="Upcoming gatherings and recaps of past events appear here."
      />

      {/* CTA — bold closing invitation */}
      <section className="relative overflow-hidden bg-gradient-to-br from-maroon-800 via-maroon-900 to-maroon-950 text-cream-100">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 text-gold-300/10 pattern-dots" />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full bg-gold-400/10 blur-3xl"
        />
        <div className="container-page relative py-16 text-center sm:py-20">
          <Reveal>
            <p className="eyebrow text-gold-400">Join Our Mission</p>
            <h2 className="mx-auto mt-3 max-w-2xl font-serif text-3xl font-bold text-cream-50 sm:text-4xl lg:text-5xl">
              Together, we empower women and girls
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-cream-200">
              Become part of a global movement for gender equality. Volunteer, partner, or begin your Zonta journey with a club near you.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link to="/contact" className="btn-gold">
                Get Involved
                <span aria-hidden="true">&rsaquo;</span>
              </Link>
              <a
                href="https://facebook.com/zontadistrict17area1"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-md border-2 border-cream-100/40 px-6 py-3 text-xs font-bold uppercase tracking-[0.16em] text-cream-100 transition hover:border-gold-400 hover:text-gold-300"
              >
                Follow on Facebook
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
